import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

// A living archive night-sky hero — matches the approved editorial mock.
// SVG-first illustration: gradient sky, sun + moon, mountains, water horizon,
// with centered display copy and two primary CTAs.

function useClockAt(zone = "America/Los_Angeles") {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const opts = { timeZone: zone, hour: "numeric", minute: "2-digit", hour12: true };
  const dateOpts = { timeZone: zone, weekday: "long", year: "numeric", month: "long", day: "numeric" };
  return {
    time: now.toLocaleTimeString("en-US", opts),
    date: now.toLocaleDateString("en-US", dateOpts),
  };
}

export default function KineticHero() {
  const { time, date } = useClockAt("America/Los_Angeles");

  return (
    <section
      data-testid="hero-section"
      className="relative w-full overflow-hidden bg-[#0A1620] text-[color:var(--np-cream,#F5EBDC)]"
    >
      {/* --- SVG illustrated night-sky backdrop --- */}
      <div className="absolute inset-0">
        <svg viewBox="0 0 1600 900" preserveAspectRatio="xMidYMid slice" className="w-full h-full">
          <defs>
            <linearGradient id="skyGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor="#0B1A26" />
              <stop offset="45%" stopColor="#122636" />
              <stop offset="70%" stopColor="#1B3A44" />
              <stop offset="100%" stopColor="#2A4A48" />
            </linearGradient>
            <linearGradient id="waterGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1D3A44" />
              <stop offset="100%" stopColor="#0F2028" />
            </linearGradient>
            <radialGradient id="sunGlow" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="#FFC873" stopOpacity="0.85" />
              <stop offset="60%" stopColor="#E4A15C" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#E4A15C" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="moonGlow" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="#F1E1FF" stopOpacity="0.55" />
              <stop offset="60%" stopColor="#8B7BA8" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#8B7BA8" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="risingSun" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="#FFB870" stopOpacity="0.85" />
              <stop offset="60%" stopColor="#B85F2A" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#B85F2A" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Sky */}
          <rect width="1600" height="700" fill="url(#skyGrad)" />

          {/* Stars */}
          {Array.from({ length: 70 }).map((_, i) => {
            const cx = (i * 137) % 1600;
            const cy = (i * 53) % 460;
            const r = ((i * 13) % 5) / 3 + 0.4;
            const op = 0.35 + ((i * 17) % 60) / 100;
            return <circle key={i} cx={cx} cy={cy} r={r} fill="#F5EBDC" opacity={op} />;
          })}

          {/* Sun (top-left) with glow */}
          <circle cx="240" cy="220" r="180" fill="url(#sunGlow)" />
          <circle cx="240" cy="220" r="58" fill="#E4A15C" />
          <circle cx="240" cy="220" r="58" fill="url(#sunGlow)" />

          {/* Crescent moon (top-right) */}
          <circle cx="1360" cy="200" r="120" fill="url(#moonGlow)" />
          <g>
            <circle cx="1360" cy="200" r="46" fill="#E9DDF3" />
            <circle cx="1378" cy="192" r="42" fill="#0B1A26" />
          </g>

          {/* Distant planet (mid-left) */}
          <circle cx="200" cy="470" r="46" fill="#E9DDD3" opacity="0.85" />
          <ellipse cx="200" cy="470" rx="70" ry="8" fill="#7A6A5C" opacity="0.5" />

          {/* Rising sun (mid-right at horizon) */}
          <circle cx="1330" cy="500" r="150" fill="url(#risingSun)" />
          <circle cx="1330" cy="500" r="52" fill="#D67A3E" opacity="0.95" />

          {/* Far mountains */}
          <path
            d="M0 560 L120 500 L240 550 L360 470 L480 540 L620 490 L760 550 L900 480 L1040 540 L1180 500 L1320 555 L1460 505 L1600 555 L1600 700 L0 700 Z"
            fill="#1A2E36" opacity="0.9"
          />
          {/* Mid mountains */}
          <path
            d="M0 620 L120 585 L260 610 L400 560 L560 600 L720 570 L860 615 L1000 570 L1160 610 L1320 580 L1460 615 L1600 590 L1600 720 L0 720 Z"
            fill="#152731" opacity="0.95"
          />
          {/* Near hills silhouette */}
          <path
            d="M0 690 L120 660 L240 680 L360 640 L480 675 L620 650 L760 680 L900 645 L1040 680 L1180 650 L1320 685 L1460 655 L1600 685 L1600 730 L0 730 Z"
            fill="#0F1F26"
          />

          {/* Water reflection */}
          <rect y="700" width="1600" height="200" fill="url(#waterGrad)" />
          {/* Water shimmer bands */}
          {Array.from({ length: 8 }).map((_, i) => (
            <rect key={i} x={0} y={720 + i * 22} width="1600" height="1" fill="#F5EBDC" opacity={0.06 + (i % 3) * 0.02} />
          ))}

          {/* Trees silhouette on shore */}
          <g fill="#0A1620">
            {Array.from({ length: 40 }).map((_, i) => {
              const x = i * 42 + (i % 3) * 6;
              const h = 18 + ((i * 7) % 14);
              return <path key={i} d={`M${x} 700 L${x + 5} ${700 - h} L${x + 10} 700 Z`} />;
            })}
          </g>
        </svg>
      </div>

      {/* Vignette scrim */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A1620]/60 pointer-events-none" />

      {/* --- Content --- */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-20 md:pb-28">
        <div className="text-center">
          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif-display font-light tracking-tight leading-[0.98] text-[color:var(--np-cream,#F5EBDC)] text-[10vw] md:text-[6.5vw] lg:text-[5.4vw] max-w-[1200px] mx-auto"
          >
            A LIVING ARCHIVE OF OUR<br />CHANGING PLANET.
          </motion.h1>

          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="mt-8 md:mt-10 max-w-2xl mx-auto text-base md:text-lg leading-relaxed text-[#E5D8C4]/85"
          >
            Document, explore, and understand Earth's dynamic ecology — mapping
            flora, fauna, weather, landscapes, and ocean shores through
            pinpoint GPS-based records. Every place. Every species. Every day.
          </motion.p>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.55 }}
            className="mt-10 flex flex-wrap items-center justify-center gap-4"
          >
            <Link
              to="/civic-reports"
              data-testid="hero-explore-map-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#F5EBDC] text-[#0A1620] px-6 py-3 font-mono-tech text-[11px] tracking-[0.3em] uppercase hover:bg-[#E4A15C] hover:text-[#0A1620] transition-colors"
            >
              Start Exploring Map →
            </Link>
            <a
              href="#identify"
              data-testid="hero-add-sighting-btn"
              className="inline-flex items-center gap-2 rounded-full border border-[#F5EBDC]/40 text-[#F5EBDC] px-6 py-3 font-mono-tech text-[11px] tracking-[0.3em] uppercase hover:bg-[#F5EBDC] hover:text-[#0A1620] transition-colors"
            >
              + Add Sighting
            </a>
          </motion.div>
        </div>
      </div>

      {/* Footer strip inside hero */}
      <div className="relative z-10 border-t border-[#F5EBDC]/10 bg-[#0A1620]/70 backdrop-blur-sm">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex flex-wrap items-center justify-between gap-3 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#E5D8C4]/80">
          <span>NaturePulse™ © 2026 · A Social Pulse Endeavor</span>
          <span>{date} · {time} · Portland, OR</span>
        </div>
      </div>
    </section>
  );
}
